/**
 * AccouPro Centralized Authoritative Financial Calculation Engine
 * 
 * Single source of truth for:
 * - Net Sales Revenue (Taxable Revenue excluding GST minus returns)
 * - Purchases (Capitalized to Inventory Asset, NOT Operating Expenses)
 * - Inventory Asset Valuation (Perpetual Inventory matching)
 * - Cost of Goods Sold (COGS recognized upon sale)
 * - Operating Expenses (Isolated from inventory purchases)
 * - Profit & Loss (Gross Profit, Operating Profit, Net Profit, Margins)
 * - GST Liability & Excess Input Tax Credit (ITC Carried Forward)
 * - Balance Sheet & Working Capital Ratios
 * - Cash Flow Analytics
 */

import { Ledger, Voucher, StockItem } from "./types";

export interface FinancialEngineParams {
  vouchers: Voucher[];
  ledgers: Ledger[];
  stockItems?: StockItem[];
  expenses?: Array<{
    id?: string;
    amount: number;
    category?: string;
    vendor?: string;
    date?: string;
    status?: string;
  }>;
  fromDate?: string;
  toDate?: string;
  asOfDate?: string;
}

export interface MonthlyTrendPoint {
  month: string;
  monthKey: string;
  sales: number;
  cogs: number;
  operatingExpenses: number;
  totalExpenses: number;
  grossProfit: number;
  netProfit: number;
  inflow: number;
  outflow: number;
  netCash: number;
}

export interface FinancialMetrics {
  // Period filters
  fromDate?: string;
  toDate?: string;
  asOfDate?: string;

  // Revenue & Sales
  grossSales: number;
  netSalesRevenue: number;
  salesVouchersCount: number;
  totalReceipts: number;

  // Purchases & Inventory
  grossPurchases: number;
  taxablePurchases: number;
  purchaseVouchersCount: number;
  totalPayments: number;
  stockAssetValue: number;
  inventoryItemsCount: number;
  inventoryTotalQty: number;

  // Cost of Goods Sold & Operating Expenses
  cogs: number;
  operatingExpenses: number;
  indirectExpenses: number;
  otherDirectExpenses: number;
  otherIncome: number;
  totalExpenses: number;

  // Profitability
  grossProfit: number;
  grossProfitMargin: number;
  operatingProfit: number;
  operatingProfitMargin: number;
  netProfit: number;
  netProfitMargin: number;
  operatingExpenseRatio: number;

  // GST Breakdown (Indian GST Law compliant)
  gstOutput: number;
  gstOutputCGST: number;
  gstOutputSGST: number;
  gstOutputIGST: number;
  gstInput: number;
  gstInputCGST: number;
  gstInputSGST: number;
  gstInputIGST: number;
  netGstDifference: number;
  netGstPayable: number;
  excessITC: number;
  gstStatusLabel: string;
  gstStatusType: "credit" | "payable" | "balanced";

  // Balance Sheet & Working Capital
  totalReceivable: number;
  totalPayable: number;
  cashAndBankBalance: number;
  currentAssets: number;
  currentLiabilities: number;
  currentRatio: number;
  quickRatio: number;
  workingCapital: number;

  // Cash Flow
  netCashFlow: number;

  // Operational Metadata
  totalVouchersCount: number;
  pendingVouchersCount: number;
  isBalanced: boolean;
}

export function computeFinancialMetrics({
  vouchers,
  ledgers,
  stockItems = [],
  expenses = [],
  fromDate,
  toDate,
  asOfDate,
}: FinancialEngineParams): FinancialMetrics {
  // 1. Filter approved vouchers by date if specified
  const approvedVouchers = vouchers.filter((v) => {
    if (v.status !== "Approved") return false;
    if (asOfDate && v.date > asOfDate) return false;
    if (fromDate && v.date < fromDate) return false;
    if (toDate && v.date > toDate) return false;
    return true;
  });

  const pendingVouchers = vouchers.filter((v) => v.status === "Pending");

  // 2. Classify vouchers
  const salesVouchers = approvedVouchers.filter((v) => v.type === "Sales");
  const purchaseVouchers = approvedVouchers.filter((v) => v.type === "Purchase");
  const receiptVouchers = approvedVouchers.filter((v) => v.type === "Receipt");
  const paymentVouchers = approvedVouchers.filter((v) => v.type === "Payment");
  const creditNotes = approvedVouchers.filter((v) => v.type === "Credit Note");
  const debitNotes = approvedVouchers.filter((v) => v.type === "Debit Note");

  // 3. GST Calculation
  let gstOutputCGST = 0;
  let gstOutputSGST = 0;
  let gstOutputIGST = 0;
  salesVouchers.forEach((v) => {
    gstOutputCGST += Number(v.cgst || 0);
    gstOutputSGST += Number(v.sgst || 0);
    gstOutputIGST += Number(v.igst || 0);
  });
  creditNotes.forEach((v) => {
    gstOutputCGST -= Number(v.cgst || 0);
    gstOutputSGST -= Number(v.sgst || 0);
    gstOutputIGST -= Number(v.igst || 0);
  });
  const gstOutput = Math.max(0, gstOutputCGST + gstOutputSGST + gstOutputIGST);

  let gstInputCGST = 0;
  let gstInputSGST = 0;
  let gstInputIGST = 0;
  purchaseVouchers.forEach((v) => {
    gstInputCGST += Number(v.cgst || 0);
    gstInputSGST += Number(v.sgst || 0);
    gstInputIGST += Number(v.igst || 0);
  });
  debitNotes.forEach((v) => {
    gstInputCGST -= Number(v.cgst || 0);
    gstInputSGST -= Number(v.sgst || 0);
    gstInputIGST -= Number(v.igst || 0);
  });
  const gstInput = Math.max(0, gstInputCGST + gstInputSGST + gstInputIGST);

  // Net GST Position
  const netGstDifference = gstOutput - gstInput;
  let netGstPayable = 0;
  let excessITC = 0;
  let gstStatusLabel = "GST Balanced (Nil Payable)";
  let gstStatusType: "credit" | "payable" | "balanced" = "balanced";

  if (netGstDifference > 0) {
    netGstPayable = Number(netGstDifference.toFixed(2));
    excessITC = 0;
    gstStatusLabel = "Net GST Payable";
    gstStatusType = "payable";
  } else if (netGstDifference < 0) {
    netGstPayable = 0;
    excessITC = Number(Math.abs(netGstDifference).toFixed(2));
    gstStatusLabel = "Excess ITC (Carried Forward)";
    gstStatusType = "credit";
  }

  // 4. Sales & Purchases
  const grossSales = salesVouchers.reduce((s, v) => s + Number(v.totalDebit || 0), 0) -
    creditNotes.reduce((s, v) => s + Number(v.totalDebit || 0), 0);

  // Net sales revenue (excluding taxes)
  let netSalesRevenue = 0;
  // If voucher lines exist, use sales ledger credits directly
  let hasLineDetails = false;
  for (const v of salesVouchers) {
    if (v.lines && v.lines.length > 0) {
      hasLineDetails = true;
      for (const line of v.lines) {
        const ledger = ledgers.find((l) => l.id === line.ledgerId);
        if (ledger && (ledger.group === "Sales Accounts" || ledger.group === "Direct Income" || ledger.group === "Revenue")) {
          netSalesRevenue += Number(line.credit || 0) - Number(line.debit || 0);
        }
      }
    }
  }
  if (!hasLineDetails || netSalesRevenue === 0) {
    netSalesRevenue = Math.max(0, grossSales - gstOutput);
  }

  const grossPurchases = purchaseVouchers.reduce((s, v) => s + Number(v.totalDebit || 0), 0) -
    debitNotes.reduce((s, v) => s + Number(v.totalDebit || 0), 0);
  const taxablePurchases = Math.max(0, grossPurchases - gstInput);

  // 5. Cost of Goods Sold (COGS)
  const cogsLedger = ledgers.find(
    (l) => l.id === "ldg-cogs-comp-f4d8fb4e" || l.name.toLowerCase().includes("cost of goods sold") || l.id.includes("cogs")
  );
  let cogs = 0;
  if (cogsLedger) {
    cogs = Number(cogsLedger.currentBalance || 0);
  } else {
    // Sum lines debited to COGS
    for (const v of approvedVouchers) {
      if (v.lines) {
        for (const line of v.lines) {
          if (line.ledgerName?.toLowerCase().includes("cost of goods sold") || line.ledgerId?.includes("cogs")) {
            cogs += Number(line.debit || 0) - Number(line.credit || 0);
          }
        }
      }
    }
  }

  // 6. Inventory Asset & Stock Items
  const stockAssetLedger = ledgers.find(
    (l) => l.group === "Stock-in-Hand" || l.id === "ldg-stock-comp-f4d8fb4e" || l.name.toLowerCase().includes("stock-in-hand")
  );
  const stockAssetValue = stockAssetLedger ? Number(stockAssetLedger.currentBalance || 0) : 0;
  const inventoryTotalQty = stockItems.reduce((s, item) => s + (item.itemType === "goods" ? Number(item.currentQty || 0) : 0), 0);

  // 7. Operating Expenses
  // Filter expenses by date if specified
  const filteredExpenses = expenses.filter((e) => {
    if (e.status === "Rejected") return false;
    if (asOfDate && e.date && e.date > asOfDate) return false;
    if (fromDate && e.date && e.date < fromDate) return false;
    if (toDate && e.date && e.date > toDate) return false;
    return true;
  });
  const operatingExpenses = filteredExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  // Other indirect/direct expense ledgers (excluding COGS and Purchase accounts)
  let otherDirectExpenses = 0;
  const directExpenseLedgers = ledgers.filter(
    (l) => l.group === "Direct Expenses" && !l.name.toLowerCase().includes("cost of goods sold") && !l.id.includes("cogs")
  );
  otherDirectExpenses = directExpenseLedgers.reduce((s, l) => s + (l.currentBalanceType === "Dr" ? Number(l.currentBalance || 0) : 0), 0);

  const indirectExpenseLedgers = ledgers.filter(
    (l) => l.group === "Indirect Expenses"
  );
  const indirectExpensesFromLedgers = indirectExpenseLedgers.reduce((s, l) => s + (l.currentBalanceType === "Dr" ? Number(l.currentBalance || 0) : 0), 0);
  const indirectExpenses = Number((operatingExpenses + indirectExpensesFromLedgers).toFixed(2));

  let otherIncome = 0;
  const incomeLedgers = ledgers.filter((l) => l.group === "Direct Income" || l.group === "Indirect Income");
  otherIncome = incomeLedgers.reduce((s, l) => s + (l.currentBalanceType === "Cr" ? Number(l.currentBalance || 0) : 0), 0);

  // Total expenses for P&L = COGS + Operating Expenses + Other Direct Expenses
  const totalExpenses = cogs + operatingExpenses + otherDirectExpenses;

  // 8. Profitability Metrics
  const grossProfit = Number((netSalesRevenue - cogs - otherDirectExpenses).toFixed(2));
  const grossProfitMargin = netSalesRevenue > 0 ? Number(((grossProfit / netSalesRevenue) * 100).toFixed(2)) : 0;

  const operatingProfit = Number((grossProfit - operatingExpenses + otherIncome).toFixed(2));
  const operatingProfitMargin = netSalesRevenue > 0 ? Number(((operatingProfit / netSalesRevenue) * 100).toFixed(2)) : 0;

  const netProfit = operatingProfit;
  const netProfitMargin = netSalesRevenue > 0 ? Number(((netProfit / netSalesRevenue) * 100).toFixed(2)) : 0;

  const operatingExpenseRatio = netSalesRevenue > 0 ? Number(((operatingExpenses / netSalesRevenue) * 100).toFixed(2)) : 0;

  // 9. Balance Sheet & Working Capital
  const debtors = ledgers.filter((l) => l.group === "Sundry Debtors");
  const creditors = ledgers.filter((l) => l.group === "Sundry Creditors");
  const totalReceivable = debtors.reduce((s, l) => s + (l.currentBalanceType === "Dr" ? Number(l.currentBalance || 0) : 0), 0);
  const totalPayable = creditors.reduce((s, l) => s + (l.currentBalanceType === "Cr" ? Number(l.currentBalance || 0) : 0), 0);

  const bankAndCash = ledgers.filter((l) => l.group === "Bank Accounts" || l.group === "Cash-in-Hand");
  const cashAndBankBalance = bankAndCash.reduce(
    (s, l) => s + (l.currentBalanceType === "Dr" ? Number(l.currentBalance || 0) : -Number(l.currentBalance || 0)),
    0
  );

  const currentAssets = cashAndBankBalance + totalReceivable + stockAssetValue + excessITC;
  const currentLiabilities = totalPayable + netGstPayable;
  const currentRatio = currentLiabilities > 0 ? Number((currentAssets / currentLiabilities).toFixed(2)) : currentAssets > 0 ? 999 : 1;
  const quickRatio = currentLiabilities > 0 ? Number(((currentAssets - stockAssetValue) / currentLiabilities).toFixed(2)) : 1;
  const workingCapital = Number((currentAssets - currentLiabilities).toFixed(2));

  // 10. Cash Flow
  const totalReceipts = receiptVouchers.reduce((s, v) => s + Number(v.totalDebit || 0), 0);
  const totalPayments = paymentVouchers.reduce((s, v) => s + Number(v.totalDebit || 0), 0);
  const netCashFlow = Number((totalReceipts - totalPayments - operatingExpenses).toFixed(2));

  return {
    fromDate,
    toDate,
    asOfDate,
    grossSales,
    netSalesRevenue,
    salesVouchersCount: salesVouchers.length,
    totalReceipts,
    grossPurchases,
    taxablePurchases,
    purchaseVouchersCount: purchaseVouchers.length,
    totalPayments,
    stockAssetValue,
    inventoryItemsCount: stockItems.length,
    inventoryTotalQty,
    cogs,
    operatingExpenses,
    indirectExpenses,
    otherDirectExpenses,
    otherIncome,
    totalExpenses,
    grossProfit,
    grossProfitMargin,
    operatingProfit,
    operatingProfitMargin,
    netProfit,
    netProfitMargin,
    operatingExpenseRatio,
    gstOutput,
    gstOutputCGST,
    gstOutputSGST,
    gstOutputIGST,
    gstInput,
    gstInputCGST,
    gstInputSGST,
    gstInputIGST,
    netGstDifference,
    netGstPayable,
    excessITC,
    gstStatusLabel,
    gstStatusType,
    totalReceivable,
    totalPayable,
    cashAndBankBalance,
    currentAssets,
    currentLiabilities,
    currentRatio,
    quickRatio,
    workingCapital,
    netCashFlow,
    totalVouchersCount: vouchers.length,
    pendingVouchersCount: pendingVouchers.length,
    isBalanced: true,
  };
}

/**
 * Computes chronological monthly trends without simulation or synthetic random data
 */
export function computeMonthlyFinancialTrends(
  vouchers: Voucher[],
  expenses: Array<{ date?: string; amount: number; status?: string }> = [],
  monthsCount = 6
): MonthlyTrendPoint[] {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = new Date();
  const points: MonthlyTrendPoint[] = [];

  for (let i = monthsCount - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const mLabel = months[d.getMonth()];

    // Sales in month
    const mSalesVouchers = vouchers.filter(
      (v) => v.type === "Sales" && v.status === "Approved" && v.date && v.date.startsWith(mKey)
    );
    const mSales = mSalesVouchers.reduce((s, v) => {
      const gst = (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0);
      return s + (v.totalDebit - gst);
    }, 0);

    // COGS in month
    const mCogsVouchers = vouchers.filter(
      (v) => v.type === "Journal" && v.status === "Approved" && v.date && v.date.startsWith(mKey)
    );
    let mCogs = 0;
    for (const v of mCogsVouchers) {
      if (v.lines) {
        for (const line of v.lines) {
          if (line.ledgerId?.includes("cogs") || line.ledgerName?.toLowerCase().includes("cost of goods sold")) {
            mCogs += Number(line.debit || 0);
          }
        }
      }
    }

    // Operating expenses in month
    const mExpenses = expenses
      .filter((e) => e.status !== "Rejected" && e.date && e.date.startsWith(mKey))
      .reduce((s, e) => s + Number(e.amount || 0), 0);

    const mTotalExpenses = mCogs + mExpenses;
    const mGrossProfit = mSales - mCogs;
    const mNetProfit = mSales - mTotalExpenses;

    // Cash flow
    const mReceipts = vouchers
      .filter((v) => (v.type === "Receipt" || v.type === "Sales") && v.status === "Approved" && v.date && v.date.startsWith(mKey))
      .reduce((s, v) => s + Number(v.totalDebit || 0), 0);
    const mPayments = vouchers
      .filter((v) => (v.type === "Payment" || v.type === "Purchase") && v.status === "Approved" && v.date && v.date.startsWith(mKey))
      .reduce((s, v) => s + Number(v.totalDebit || 0), 0);
    const mOutflow = mPayments + mExpenses;

    points.push({
      month: mLabel,
      monthKey: mKey,
      sales: mSales,
      cogs: mCogs,
      operatingExpenses: mExpenses,
      totalExpenses: mTotalExpenses,
      grossProfit: mGrossProfit,
      netProfit: mNetProfit,
      inflow: mReceipts,
      outflow: mOutflow,
      netCash: mReceipts - mOutflow,
    });
  }

  return points;
}
